<template>
  <div class="shop-page">
    <!-- 1. Barra de anuncio -->
    <div class="announcement">
      Envíos a toda Colombia · Mínimo 6 unidades por pedido
    </div>

    <!-- 2. Header sticky -->
    <header class="header" :class="{ 'header--scrolled': headerScrolled }">
      <div class="container-x">
        <div class="header__inner">
          <div class="header__brand">
            <img
              :src="storeLogoUrl"
              alt="Logo"
              class="brand-logo brand-logo--sm"
              @error="logoBroken = true"
            />
            <span class="header__name">{{ companyName }}</span>
          </div>

          <label class="header__search">
            <i class="fas fa-search"></i>
            <input
              v-model="search"
              type="text"
              placeholder="Buscar producto, ref o marca…"
            />
            <button
              v-if="search"
              type="button"
              class="header__search-clear"
              aria-label="Limpiar búsqueda"
              @click="search = ''"
            >
              <i class="fas fa-times"></i>
            </button>
          </label>

          <button type="button" class="header__cart" aria-label="Ver carrito" @click="openCart">
            <i class="fas fa-shopping-bag"></i>
            <span v-if="cartCount > 0" class="header__cart-bubble">{{ bubbleLabel }}</span>
          </button>
        </div>

        <div class="header__row2">
          <label class="header__search header__search--mobile">
            <i class="fas fa-search"></i>
            <input
              v-model="search"
              type="text"
              placeholder="Buscar producto, ref o marca…"
            />
            <button
              v-if="search"
              type="button"
              class="header__search-clear"
              aria-label="Limpiar búsqueda"
              @click="search = ''"
            >
              <i class="fas fa-times"></i>
            </button>
          </label>
        </div>
      </div>
    </header>

    <!-- Cargando vendedor -->
    <main v-if="loadingSeller" class="collection">
      <div class="container-x">
        <div class="state state--page">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
      </div>
    </main>

    <!-- Vendedor no encontrado -->
    <main v-else-if="sellerError" class="collection">
      <div class="container-x">
        <div class="state state--page">
          <i class="fas fa-exclamation-circle"></i>
          <p>{{ sellerError }}</p>
        </div>
      </div>
    </main>

    <!-- 3. Catálogo -->
    <main v-else class="collection">
      <div class="container-x">
        <div class="collection__head">
          <h1>Catálogo Mayorista</h1>
          <p>
            Atendido por <strong>{{ sellerName }}</strong> · Pedidos por WhatsApp
          </p>
        </div>

        <!-- 4. Toolbar -->
        <div class="toolbar">
          <span class="toolbar__count">{{ countLabel }}</span>
          <label class="toolbar__sort">
            <span class="toolbar__sort-label">Ordenar por</span>
            <span class="select-wrap">
              <select v-model="sortBy">
                <option value="featured">Destacados</option>
                <option value="az">Alfabéticamente A–Z</option>
                <option value="za">Z–A</option>
                <option value="price_asc">Precio menor a mayor</option>
                <option value="price_desc">Precio mayor a menor</option>
                <option value="stock_desc">Mayor stock</option>
              </select>
              <i class="fas fa-chevron-down"></i>
            </span>
          </label>
        </div>

        <!-- 5. Chips de categorías -->
        <div v-if="categories.length" class="cat-chips">
          <button
            type="button"
            class="cat-chip"
            :class="{ 'cat-chip--active': activeCategory === '' }"
            @click="setCategory('')"
          >
            Todas
          </button>
          <button
            v-for="category in categories"
            :key="category.id"
            type="button"
            class="cat-chip"
            :class="{ 'cat-chip--active': activeCategory === String(category.id) }"
            @click="setCategory(String(category.id))"
          >
            {{ category.name }}
          </button>
        </div>

        <!-- 7a. Skeleton -->
        <div v-if="loadingProducts" class="pgrid">
          <div v-for="i in 8" :key="i" class="skel-card">
            <div class="skel skel--media"></div>
            <div class="skel-card__lines">
              <div class="skel skel--line" style="width: 40%"></div>
              <div class="skel skel--line" style="width: 80%"></div>
              <div class="skel skel--line" style="width: 25%"></div>
            </div>
          </div>
        </div>

        <!-- 7b. Error -->
        <div v-else-if="productsError" class="state">
          <i class="fas fa-exclamation-circle"></i>
          <p>{{ productsError }}</p>
        </div>

        <!-- 7c. Vacío -->
        <div v-else-if="products.length === 0" class="state">
          <i class="fas fa-search"></i>
          <p>No se encontraron productos</p>
          <button type="button" class="state__link" @click="clearFilters">
            Limpiar búsqueda
          </button>
        </div>

        <!-- 6. Grilla de productos -->
        <template v-else>
          <div class="pgrid">
            <article v-for="product in products" :key="product.id" class="pcard">
              <div class="pcard__media">
                <img
                  v-if="productImageUrl(product)"
                  :src="productImageUrl(product)"
                  :alt="product.name"
                  loading="lazy"
                  @error="onProductImageError(product)"
                />
                <div v-else class="pcard__placeholder">
                  <i class="fas fa-tshirt"></i>
                </div>

                <span v-if="isSoldOut(product)" class="badge--soldout">Agotado</span>
                <span v-else-if="hasStockBadge(product)" class="badge--stock">
                  {{ stockLabel(product) }}
                </span>

                <button
                  type="button"
                  class="pcard__quick"
                  :class="{ 'pcard__quick--soldout': isSoldOut(product) }"
                  :disabled="isSoldOut(product)"
                  @click="quickAdd(product)"
                >
                  <i class="fas" :class="isSoldOut(product) ? 'fa-ban' : 'fa-plus'"></i>
                  {{ isSoldOut(product) ? 'Agotado' : 'Agregar' }}
                </button>
              </div>

              <div class="pcard__info">
                <h3 class="pcard__title">{{ product.name }}</h3>
                <div class="pcard__price-row">
                  <span class="pcard__price">{{ formatCop(unitPrice(product)) }}</span>
                  <span class="tag tag--cop">COP</span>
                  <span v-if="trm > 0" class="tag tag--usd">
                    USD {{ formatUsd(unitPrice(product)) }}
                  </span>
                </div>
                <p class="pcard__ref">Ref. {{ product.ref || '—' }}</p>
              </div>
            </article>
          </div>

          <div v-if="loadingMore" class="loadmore">
            <i class="fas fa-spinner fa-spin"></i>
          </div>
          <div v-else-if="!hasMore" class="grid-end"></div>
          <div ref="sentinelEl" class="sentinel"></div>
        </template>
      </div>
    </main>

    <!-- 8. Footer -->
    <footer class="shop-footer">
      <img
        :src="storeLogoUrl"
        alt="Logo"
        class="brand-logo brand-logo--footer"
        @error="logoBroken = true"
      />
      <p class="shop-footer__name">{{ companyName }}</p>
      <p class="shop-footer__tagline">Catálogo mayorista · Mínimo 6 unidades por pedido</p>
      <p class="shop-footer__copy">© {{ year }} {{ companyName }}. Todos los derechos reservados.</p>
    </footer>

    <!-- 9. Drawer del carrito -->
    <div
      class="cart-overlay"
      :class="{ 'cart-overlay--open': cartOpen }"
      @click="closeCart"
    ></div>

    <aside class="cart" :class="{ 'cart--open': cartOpen }" aria-label="Carrito de compras">
      <div class="cart__head">
        <h2>Tu carrito ({{ cartCount }})</h2>
        <button type="button" class="icon-btn" aria-label="Cerrar carrito" @click="closeCart">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div v-if="cart.length === 0" class="cart__empty">
        <i class="fas fa-shopping-bag"></i>
        <p>Tu carrito está vacío</p>
        <button type="button" class="btn--dark" @click="closeCart">
          Continuar comprando
        </button>
      </div>

      <div v-else class="cart__items">
        <div v-for="(item, index) in cart" :key="item.id" class="cart__item">
          <div class="cart__thumb">
            <img
              v-if="item.image_url && !brokenImages[item.id]"
              :src="item.image_url"
              :alt="item.name"
              @error="onProductImageError(item)"
            />
            <i v-else class="fas fa-tshirt"></i>
          </div>

          <div class="cart__item-info">
            <p class="cart__item-name">{{ item.name }}</p>
            <p class="cart__item-unit">{{ formatCop(unitPrice(item)) }} c/u</p>
            <div class="cart__item-actions">
              <div class="stepper">
                <button type="button" aria-label="Quitar una unidad" @click="changeQty(index, -1)">
                  <i class="fas fa-minus"></i>
                </button>
                <span>{{ item.quantity }}</span>
                <button type="button" aria-label="Agregar una unidad" @click="changeQty(index, 1)">
                  <i class="fas fa-plus"></i>
                </button>
              </div>
              <button type="button" class="cart__item-remove" @click="removeFromCart(index)">
                Eliminar
              </button>
            </div>
          </div>

          <div class="cart__item-total">{{ formatCop(lineTotal(item)) }}</div>
        </div>
      </div>

      <div v-if="cart.length > 0" class="cart__foot">
        <div class="minbar">
          <div class="minbar__rail">
            <div class="minbar__fill" :style="{ width: minProgress + '%' }"></div>
          </div>
          <p class="minbar__text" :class="{ 'minbar__text--ok': minMet }">
            {{ cartCount }}/{{ minTotalUnidades }} unidades · mínimo por pedido
          </p>
        </div>

        <div class="cart__subtotal">
          <span class="cart__subtotal-label">Subtotal</span>
          <div class="cart__subtotal-value">
            <strong>{{ formatCop(cartTotal) }}</strong>
            <span class="tag tag--cop">COP</span>
            <span v-if="trm > 0" class="tag tag--usd">USD {{ formatUsd(cartTotal) }}</span>
          </div>
        </div>

        <p class="cart__note">El pedido se envía y confirma por WhatsApp.</p>

        <form class="cart__form" @submit.prevent="submitOrder">
          <input
            v-model.trim="form.nombre"
            type="text"
            class="field"
            placeholder="Nombre completo *"
            autocomplete="name"
          />
          <div class="cart__form-row">
            <select
              v-model="form.departamento"
              class="field"
              :class="{ 'field--placeholder': form.departamento === '' }"
              autocomplete="address-level1"
            >
              <option value="">Departamento *</option>
              <option v-for="dpto in departamentos" :key="dpto" :value="dpto">
                {{ dpto }}
              </option>
            </select>
            <select
              v-model="form.ciudad"
              class="field"
              :disabled="form.departamento === ''"
              :class="{ 'field--placeholder': form.ciudad === '' }"
              autocomplete="address-level2"
            >
              <option value="">Ciudad *</option>
              <option v-for="city in ciudadesFiltradas" :key="city" :value="city">
                {{ city }}
              </option>
            </select>
          </div>
          <input
            v-model.trim="form.direccion"
            type="text"
            class="field"
            placeholder="Dirección *"
            autocomplete="street-address"
          />

          <button type="submit" class="btn--dark btn--block" :disabled="!canSubmit || sending">
            <template v-if="sending">
              <i class="fas fa-spinner fa-spin"></i> Enviando…
            </template>
            <template v-else>
              <i class="fab fa-whatsapp"></i> Enviar pedido por WhatsApp
            </template>
          </button>

          <p v-if="orderError" class="cart__error">{{ orderError }}</p>
        </form>
      </div>
    </aside>
  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars */
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore as useVuexStore } from "vuex";
import storeService from "@/services/store.service";
import logoDefault from "@/assets/img/icon.png";
import { playCartAddSound, closeCartAddSound } from "@/utils/cartAddSound";

const MIN_TOTAL_UNIDADES = 6;
const PER_PAGE = 24;

const route = useRoute();
const vuex = useVuexStore();

const sellerToken = ref("");
const seller = ref({});
const loadingSeller = ref(true);
const sellerError = ref("");

const products = ref([]);
const loadingProducts = ref(true);
const loadingMore = ref(false);
const productsError = ref("");
const totalProducts = ref(0);
const currentPage = ref(1);
const lastPage = ref(1);

const search = ref("");
const sortBy = ref("featured");
const activeCategory = ref("");
const categories = ref([]);

const trm = ref(0);
const logoBroken = ref(false);
const headerScrolled = ref(false);
const brokenImages = reactive({});

const cart = ref([]);
const cartOpen = ref(false);
const sending = ref(false);
const orderError = ref("");
const minTotalUnidades = MIN_TOTAL_UNIDADES;

const form = reactive({
  nombre: "",
  departamento: "",
  ciudad: "",
  direccion: "",
});

const ciudades = ref([]);
const sentinelEl = ref(null);

let searchTimer = null;
let observer = null;

const companyName = computed(
  () => seller.value.company_name || seller.value.name || "Catálogo"
);
const sellerName = computed(() => seller.value.name || "—");
const storeLogoUrl = computed(() => {
  if (logoBroken.value || !seller.value.logo_url) return logoDefault;
  return seller.value.logo_url;
});
const year = computed(() => new Date().getFullYear());
const countLabel = computed(() =>
  `${totalProducts.value} ${totalProducts.value === 1 ? "producto" : "productos"}`
);

const cartCount = computed(() =>
  cart.value.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0)
);
const bubbleLabel = computed(() => (cartCount.value > 99 ? "99+" : String(cartCount.value)));
const cartTotal = computed(() =>
  cart.value.reduce((sum, item) => sum + lineTotal(item), 0)
);
const minMet = computed(() => cartCount.value >= MIN_TOTAL_UNIDADES);
const minProgress = computed(() =>
  Math.min(100, Math.round((cartCount.value / MIN_TOTAL_UNIDADES) * 100))
);
const canSubmit = computed(
  () =>
    form.nombre !== "" &&
    form.departamento !== "" &&
    form.ciudad !== "" &&
    form.direccion !== ""
);

const departamentos = computed(() => {
  const set = new Set(ciudades.value.map((c) => c.DPTO));
  return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
});
const ciudadesFiltradas = computed(() => {
  if (!form.departamento) return [];
  return ciudades.value
    .filter((c) => c.DPTO === form.departamento)
    .map((c) => c.NOMBRE)
    .sort((a, b) => a.localeCompare(b, "es"));
});

function unitPrice(item) {
  const price = parseFloat(item.precio1 ?? item.sale_price ?? 0) || 0;
  const discount = parseFloat(item.descuento) || 0;
  if (discount <= 0) return price;
  const qty = parseFloat(item.quantity) || 0;
  const minUnits = parseInt(item.descuento_min_unidades, 10) || 1;
  return qty >= minUnits ? price * (1 - discount / 100) : price;
}

function lineTotal(item) {
  return (parseFloat(item.quantity) || 0) * unitPrice(item);
}

const copFormatter = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
function formatCop(value) {
  return copFormatter.format(parseFloat(value || 0));
}
function formatUsd(value) {
  return (parseFloat(value || 0) / trm.value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function isSoldOut(product) {
  return Boolean(product.manage_stock) && (parseFloat(product.quantity) || 0) <= 0;
}
function hasStockBadge(product) {
  return Boolean(product.manage_stock);
}
function stockLabel(product) {
  const qty = Math.floor(parseFloat(product.quantity) || 0);
  return qty >= 1000 ? "Stock: 999+" : `Stock: ${qty}`;
}
function productImageUrl(product) {
  if (brokenImages[product.id]) return null;
  return product.image_url || null;
}
function onProductImageError(product) {
  brokenImages[product.id] = true;
}

async function initStore() {
  sellerToken.value = String(route.query.seller || "").trim();
  logoBroken.value = false;
  if (!sellerToken.value) {
    sellerError.value = "Enlace inválido. Falta el identificador del vendedor.";
    loadingSeller.value = false;
    return;
  }

  loadingSeller.value = true;
  sellerError.value = "";
  try {
    const res = await storeService.getSeller(sellerToken.value);
    seller.value = res.data || {};
    loadingSeller.value = false;
    loadCities();
    await Promise.all([loadFilters(), resetAndLoad()]);
  } catch (error) {
    sellerError.value =
      error?.response?.data?.message || "No se pudo cargar la tienda del vendedor.";
    loadingSeller.value = false;
  }
}

async function loadFilters() {
  try {
    const res = await storeService.getFilters(sellerToken.value);
    categories.value = Array.isArray(res.data?.categories) ? res.data.categories : [];
  } catch (error) {
    categories.value = [];
  }
}

async function resetAndLoad() {
  currentPage.value = 1;
  products.value = [];
  totalProducts.value = 0;
  productsError.value = "";
  loadingProducts.value = true;
  await loadPage(1, false);
  loadingProducts.value = false;
  await nextTick();
  syncObserver();
}

async function loadPage(page, append) {
  try {
    const res = await storeService.getProducts(sellerToken.value, {
      page,
      per_page: PER_PAGE,
      search: search.value || undefined,
      category_id: activeCategory.value || undefined,
      sort: sortBy.value,
    });
    const data = res.data || {};
    const items = Array.isArray(data.data) ? data.data : [];
    products.value = append ? [...products.value, ...items] : items;
    totalProducts.value = data.total || 0;
    currentPage.value = data.current_page || page;
    lastPage.value = data.last_page || 1;
    productsError.value = "";
  } catch (error) {
    if (!append) products.value = [];
    productsError.value =
      error?.response?.data?.message || "No se pudieron cargar los productos.";
  }
}

function hasMorePages() {
  return currentPage.value < lastPage.value;
}
const hasMore = computed(hasMorePages);

function loadMore() {
  if (loadingProducts.value || loadingMore.value || !hasMorePages() || productsError.value) {
    return;
  }
  loadingMore.value = true;
  loadPage(currentPage.value + 1, true).finally(() => {
    loadingMore.value = false;
  });
}

function syncObserver() {
  if (!observer || !sentinelEl.value) return;
  observer.disconnect();
  if (products.value.length && !productsError.value) {
    observer.observe(sentinelEl.value);
  }
}

function setCategory(id) {
  if (activeCategory.value === id) return;
  activeCategory.value = id;
}

function clearFilters() {
  search.value = "";
  activeCategory.value = "";
  sortBy.value = "featured";
}

function quickAdd(product) {
  if (isSoldOut(product)) return;
  const existing = cart.value.find((item) => item.id === product.id);
  if (existing) {
    existing.quantity += 1;
  } else {
    cart.value.push({
      id: product.id,
      name: product.name,
      ref: product.ref,
      precio1: product.precio1,
      descuento: product.descuento,
      descuento_min_unidades: product.descuento_min_unidades,
      image_url: product.image_url,
      quantity: 1,
    });
  }
  playCartAddSound();
  openCart();
}

function openCart() {
  cartOpen.value = true;
}
function closeCart() {
  cartOpen.value = false;
  orderError.value = "";
}

function changeQty(index, delta) {
  const item = cart.value[index];
  if (!item) return;
  const next = (parseFloat(item.quantity) || 0) + delta;
  if (next <= 0) {
    removeFromCart(index);
    return;
  }
  item.quantity = next;
}

function removeFromCart(index) {
  cart.value.splice(index, 1);
  if (cart.value.length === 0) closeCart();
}

async function loadCities() {
  try {
    const res = await fetch(`${process.env.BASE_URL}ciudades.json?v=${Date.now()}`);
    const data = await res.json();
    ciudades.value = Array.isArray(data) ? data : [];
  } catch (error) {
    ciudades.value = [];
  }
}

function normalizePhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `57${digits}`;
  return digits;
}

function buildWhatsAppMessage() {
  const lines = [
    `*Nuevo pedido — ${companyName.value}*`,
    "",
    `Vendedor: ${sellerName.value}`,
    `Cliente: ${form.nombre}`,
    `Departamento: ${form.departamento}`,
    `Ciudad: ${form.ciudad}`,
    `Dirección: ${form.direccion}`,
    "",
    "*Productos:*",
  ];

  cart.value.forEach((item) => {
    lines.push(
      `• ${item.name}${item.ref ? ` (Ref. ${item.ref})` : ""} × ${item.quantity} — ${formatCop(lineTotal(item))}`
    );
  });

  lines.push(
    "",
    `*Total: ${formatCop(cartTotal.value)}*`,
    `Unidades: ${cartCount.value}`
  );

  return lines.join("\r\n");
}

async function submitOrder() {
  orderError.value = "";

  if (cartCount.value < MIN_TOTAL_UNIDADES) {
    orderError.value = `El pedido mínimo es de ${MIN_TOTAL_UNIDADES} unidades. Te ${
      MIN_TOTAL_UNIDADES - cartCount.value === 1 ? "falta 1 unidad" : `faltan ${MIN_TOTAL_UNIDADES - cartCount.value} unidades`
    }.`;
    return;
  }

  const phone = normalizePhone(seller.value.phone);
  if (!phone) {
    orderError.value = "El vendedor no tiene WhatsApp configurado.";
    return;
  }
  if (ciudades.value.length === 0) {
    orderError.value = "No se pudieron cargar las ciudades. Recarga la página.";
    return;
  }

  sending.value = true;
  try {
    const text = encodeURIComponent(buildWhatsAppMessage());
    window.open(`https://wa.me/${phone}?text=${text}`, "_blank", "noopener,noreferrer");
  } finally {
    sending.value = false;
  }
}

function onScroll() {
  headerScrolled.value = window.scrollY > 4;
}

watch(
  () => route.query.seller,
  (value) => {
    if (value && value !== sellerToken.value) {
      cart.value = [];
      closeCart();
      initStore();
    }
  }
);

watch(search, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    resetAndLoad();
  }, 350);
});

watch([sortBy, activeCategory], () => {
  resetAndLoad();
});

watch(cartOpen, (open) => {
  document.body.style.overflow = open ? "hidden" : "";
});

onMounted(() => {
  vuex.state.showNavbar = false;
  vuex.state.showSidenav = false;
  vuex.state.showFooter = false;
  vuex.state.hideConfigButton = true;

  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();

  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadMore();
      }
    },
    { rootMargin: "600px 0px" }
  );

  initStore();
});

onBeforeUnmount(() => {
  clearTimeout(searchTimer);
  window.removeEventListener("scroll", onScroll);
  if (observer) observer.disconnect();
  document.body.style.overflow = "";
  closeCartAddSound();
  vuex.state.showNavbar = true;
  vuex.state.showSidenav = true;
  vuex.state.showFooter = true;
  vuex.state.hideConfigButton = false;
});
</script>

<style scoped>
.shop-page {
  --ink: #121212;
  --ink-75: rgba(18, 18, 18, 0.75);
  --ink-55: rgba(18, 18, 18, 0.55);
  --line: rgba(18, 18, 18, 0.1);
  --brand: #ec91ba;
  --brand-dark: #d478a8;

  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #fff;
  font-family: "Assistant", "Segoe UI", system-ui, sans-serif;
  color: var(--ink);
}

.shop-page input,
.shop-page select,
.shop-page button,
.shop-page textarea {
  font: inherit;
}

.shop-page h1,
.shop-page h2,
.shop-page h3 {
  color: var(--ink);
}

.container-x {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
}

@media (min-width: 768px) {
  .container-x {
    padding: 0 32px;
  }
}

/* 1. Barra de anuncio */
.announcement {
  background: var(--ink);
  color: #fff;
  text-align: center;
  padding: 8px 16px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

/* 2. Header */
.header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #fff;
  border-bottom: 1px solid var(--line);
  transition: box-shadow 0.25s ease;
}

.header--scrolled {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.header__inner {
  display: flex;
  align-items: center;
  gap: 16px;
  height: 64px;
}

.brand-logo {
  border-radius: 50%;
  border: 1px solid var(--line);
  padding: 2px;
  object-fit: cover;
  background: #fff;
  flex-shrink: 0;
}

.brand-logo--sm {
  width: 40px;
  height: 40px;
}

.brand-logo--footer {
  width: 44px;
  height: 44px;
}

.header__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.header__name {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header__search {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 340px;
  margin-left: auto;
}

.header__search > .fa-search {
  position: absolute;
  left: 14px;
  font-size: 13px;
  color: var(--ink-55);
  pointer-events: none;
}

.header__search input {
  width: 100%;
  border: 1px solid rgba(18, 18, 18, 0.2);
  border-radius: 999px;
  padding: 9px 34px 9px 36px;
  font-size: 13.5px;
  background: #fff;
  outline: none;
  transition: border-color 0.2s ease;
}

.header__search input::placeholder {
  color: var(--ink-55);
}

.header__search input:focus {
  border-color: var(--ink);
}

.header__search-clear {
  position: absolute;
  right: 6px;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ink-55);
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.header__search-clear:hover {
  background: rgba(18, 18, 18, 0.08);
  color: var(--ink);
}

.header__cart {
  position: relative;
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 50%;
  background: transparent;
  font-size: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s ease;
}

.header__cart:hover {
  background: rgba(18, 18, 18, 0.05);
}

.header__cart-bubble {
  position: absolute;
  top: -3px;
  right: -3px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--brand);
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  box-shadow: 0 0 0 2px #fff;
}

.header__row2 {
  display: none;
}

@media (max-width: 749.98px) {
  .header__inner {
    height: 58px;
  }

  .header__inner .header__search {
    display: none;
  }

  .header__inner .header__cart {
    margin-left: auto;
  }

  .header__row2 {
    display: flex;
    padding-bottom: 12px;
  }

  .header__search--mobile {
    max-width: none;
    margin-left: 0;
  }
}

/* 3. Colección */
.collection {
  padding-top: 36px;
  flex: 1;
}

.collection__head {
  text-align: center;
  margin-bottom: 28px;
}

.collection__head h1 {
  margin: 0 0 6px;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.collection__head p {
  margin: 0;
  font-size: 13.5px;
  color: var(--ink-55);
}

/* 4. Toolbar */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.toolbar__count {
  font-size: 13px;
  color: var(--ink-55);
}

.toolbar__sort {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar__sort-label {
  font-size: 12.5px;
  color: var(--ink-55);
}

.select-wrap {
  position: relative;
  display: inline-flex;
}

.select-wrap select {
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid rgba(18, 18, 18, 0.2);
  border-radius: 8px;
  background: #fff;
  padding: 8px 30px 8px 12px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  outline: none;
}

.select-wrap select:focus {
  border-color: var(--ink);
}

.select-wrap > .fa-chevron-down {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 10px;
  color: var(--ink-55);
  pointer-events: none;
}

@media (max-width: 575.98px) {
  .toolbar__sort-label {
    display: none;
  }
}

/* 5. Chips */
.cat-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.cat-chip {
  border: 1px solid rgba(18, 18, 18, 0.2);
  background: #fff;
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.cat-chip:hover {
  border-color: var(--ink);
}

.cat-chip--active {
  background: var(--ink);
  border-color: var(--ink);
  color: #fff;
}

/* 6. Grilla */
.pgrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px 12px;
}

@media (min-width: 750px) {
  .pgrid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 36px 16px;
  }
}

@media (min-width: 1100px) {
  .pgrid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.pcard {
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  transition: box-shadow 0.3s ease;
}

.pcard:hover {
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.08);
}

.pcard__media {
  position: relative;
  aspect-ratio: 3 / 4;
  border-radius: 12px;
  overflow: hidden;
  background: #f6f6f6;
}

.pcard__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.35s ease;
}

.pcard:hover .pcard__media img {
  transform: scale(1.04);
}

.pcard__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pcard__placeholder .fa-tshirt {
  font-size: 38px;
  color: rgba(18, 18, 18, 0.15);
}

.badge--soldout {
  position: absolute;
  top: 10px;
  left: 10px;
  background: #fff;
  color: var(--ink);
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  line-height: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.badge--stock {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(18, 18, 18, 0.72);
  color: #fff;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

.pcard__quick {
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: none;
  border-radius: 8px;
  background: var(--ink);
  color: #fff;
  padding: 11px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: pointer;
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.25s ease, transform 0.25s ease, background 0.25s ease;
}

.pcard:hover .pcard__quick,
.pcard__quick:focus-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (hover: none) {
  .pcard__quick {
    opacity: 1;
    transform: none;
  }
}

.pcard__quick--soldout {
  background: rgba(18, 18, 18, 0.08);
  color: rgba(18, 18, 18, 0.45);
  cursor: not-allowed;
}

.pcard__info {
  padding: 12px 2px 0;
  text-align: left;
}

.pcard__title {
  margin: 0;
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pcard__price-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.pcard__price {
  font-size: 14.5px;
  font-weight: 700;
}

.tag {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
}

.tag--cop {
  background: #eeeef4;
  color: #5c5c72;
  border: 1px solid #d6d6e0;
}

.tag--usd {
  background: #e8f5ec;
  color: #1a7a3c;
  border: 1px solid #bfe3cb;
}

.pcard__ref {
  margin: 4px 0 0;
  font-size: 11.5px;
  color: var(--ink-55);
}

/* 7. Estados */
.skel-card {
  display: flex;
  flex-direction: column;
}

.skel {
  position: relative;
  background: #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
}

.skel::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0,
    rgba(255, 255, 255, 0.6) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 1.4s infinite;
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

.skel--media {
  aspect-ratio: 3 / 4;
  border-radius: 12px;
}

.skel-card__lines {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 2px 0;
}

.skel--line {
  height: 10px;
}

.state {
  padding: 56px 16px;
  text-align: center;
  color: var(--ink-55);
}

.state--page {
  padding: 120px 16px;
}

.state i {
  display: block;
  font-size: 34px;
  opacity: 0.5;
  margin-bottom: 12px;
}

.state i.fa-spinner {
  font-size: 22px;
  opacity: 0.6;
}

.state p {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
}

.state__link {
  border: none;
  background: none;
  padding: 0;
  margin-top: 10px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink);
  text-decoration: underline;
  cursor: pointer;
}

.state__link:hover {
  color: var(--brand-dark);
}

.loadmore {
  display: flex;
  justify-content: center;
  padding: 28px 0 8px;
  color: var(--ink-55);
  font-size: 18px;
}

.grid-end {
  height: 8px;
}

.sentinel {
  height: 1px;
}

/* 8. Footer */
.shop-footer {
  border-top: 1px solid var(--line);
  margin-top: 48px;
  padding: 40px 20px 48px;
  text-align: center;
}

.shop-footer__name {
  margin: 12px 0 4px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.shop-footer__tagline {
  margin: 0 0 6px;
  font-size: 12.5px;
  color: var(--ink-55);
}

.shop-footer__copy {
  margin: 0;
  font-size: 11.5px;
  color: rgba(18, 18, 18, 0.4);
}

/* 9. Drawer del carrito */
.cart-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(18, 18, 18, 0.45);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.28s ease;
}

.cart-overlay--open {
  opacity: 1;
  pointer-events: auto;
}

.cart {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 201;
  width: 100%;
  max-width: 430px;
  height: 100dvh;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.14);
  transform: translateX(100%);
  visibility: hidden;
  transition: transform 0.28s ease, visibility 0s linear 0.28s;
}

.cart--open {
  transform: translateX(0);
  visibility: visible;
  transition: transform 0.28s ease;
}

.cart__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--line);
  flex-shrink: 0;
}

.cart__head h2 {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
}

.icon-btn {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ink);
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;
}

.icon-btn:hover {
  background: rgba(18, 18, 18, 0.06);
}

.cart__empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  text-align: center;
}

.cart__empty i {
  font-size: 34px;
  opacity: 0.4;
}

.cart__empty p {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
}

.cart__items {
  flex: 1;
  overflow-y: auto;
  padding: 4px 20px;
}

.cart__item {
  display: flex;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid rgba(18, 18, 18, 0.06);
}

.cart__item:last-child {
  border-bottom: none;
}

.cart__thumb {
  width: 68px;
  height: 88px;
  border-radius: 10px;
  background: #f6f6f6;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cart__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cart__thumb .fa-tshirt {
  font-size: 22px;
  color: rgba(18, 18, 18, 0.15);
}

.cart__item-info {
  flex: 1;
  min-width: 0;
}

.cart__item-name {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.cart__item-unit {
  margin: 3px 0 0;
  font-size: 11.5px;
  color: var(--ink-55);
}

.cart__item-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 9px;
}

.cart__item-remove {
  border: none;
  background: none;
  padding: 0;
  font-size: 11.5px;
  color: var(--ink);
  text-decoration: underline;
  cursor: pointer;
}

.cart__item-remove:hover {
  color: #b3261e;
}

.cart__item-total {
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

/* Stepper */
.stepper {
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(18, 18, 18, 0.2);
  border-radius: 8px;
  overflow: hidden;
}

.stepper button {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--ink);
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
}

.stepper button:hover {
  background: rgba(18, 18, 18, 0.06);
}

.stepper span {
  min-width: 30px;
  text-align: center;
  font-size: 12.5px;
  font-weight: 700;
}

/* Pie del carrito */
.cart__foot {
  border-top: 1px solid var(--line);
  padding: 16px 20px 20px;
  max-height: 62vh;
  overflow-y: auto;
  flex-shrink: 0;
}

.minbar__rail {
  height: 3px;
  border-radius: 999px;
  background: #eee;
  overflow: hidden;
}

.minbar__fill {
  height: 100%;
  border-radius: 999px;
  background: var(--brand);
  transition: width 0.3s ease;
}

.minbar__text {
  margin: 6px 0 0;
  font-size: 10.5px;
  color: var(--ink-55);
}

.minbar__text--ok {
  color: #1a7f4b;
  font-weight: 600;
}

.cart__subtotal {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
}

.cart__subtotal-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ink-55);
}

.cart__subtotal-value {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.cart__subtotal-value strong {
  font-size: 18px;
  font-weight: 700;
}

.cart__note {
  margin: 8px 0 12px;
  font-size: 11.5px;
  color: var(--ink-55);
}

.cart__form {
  display: grid;
  gap: 8px;
}

.cart__form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.field {
  width: 100%;
  border: 1px solid rgba(18, 18, 18, 0.25);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  background: #fff;
  outline: none;
  transition: border-color 0.2s ease;
}

.field::placeholder {
  color: var(--ink-55);
}

.field:focus {
  border-color: var(--ink);
}

.field:disabled {
  background: #f6f6f6;
  color: var(--ink-55);
  cursor: not-allowed;
}

select.field {
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23121212' stroke-opacity='.55' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 30px;
}

.field--placeholder {
  color: var(--ink-55);
}

.btn--dark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 8px;
  background: var(--ink);
  color: #fff;
  padding: 14px 22px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.btn--dark:hover:not(:disabled) {
  opacity: 0.88;
}

.btn--dark:disabled {
  background: #e5e5e5;
  color: #9a9a9a;
  cursor: not-allowed;
}

.btn--block {
  width: 100%;
}

.cart__error {
  margin: 0;
  font-size: 12px;
  color: #b3261e;
}
</style>
